import { convertEmptyStringsToNull } from "../../../pages/activityBuilder/modules/inputs/utils";
import type { BuildingPayload, BuildingResponse, ModuleFull, OtherCreatePayload, OtherDeletePayload, OtherPayload, OtherResponse, RoadCreatePayload, RoadDeletePayload, RoadPayload, RoadResponse, SettlementsResponse } from "../../../pages/activityBuilder/modules/settlements/settlementsTypes";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { FEModules, FEModulesObj } from "../../../utils/moduleList";
import { transformModuleResponse } from "./factories/moduleApiUtils";
import { createEntriesGetApi, createModuleGetApi } from "./factories/createModuleApi";

// Inlined to avoid importing the page module config (settlements.ts), which pulls in
// getOptions → store and creates a circular dependency that drops mutation exports.
const includedPropList = [
	"id",
	"module_type",
	"activity",
	"land_use_change?",
	"status",
	"note",
	"start_year",
	"area",
	"ha_start",
	"ha_w",
	"ha_wo",
	"land_use_type_start",
	"land_use_type_w",
	"land_use_type_wo",
	"settlement_type_start",
	"settlement_type_w",
	"settlement_type_wo",
	"land_use_type_thread",
	"ha_thread",
	"settlement_type_thread",
];
const includedT2PropList = [
	"biomass_t2_start",
	"biomass_t2_w",
	"biomass_t2_wo",
	"soc_t2_start",
	"soc_t2_w",
	"soc_t2_wo",
	"fi_t2_start",
	"fi_t2_w",
	"fi_t2_wo",
	"fmg_t2_start",
	"fmg_t2_w",
	"fmg_t2_wo",
	"soil_carbon_t2_start",
	"soil_carbon_t2_w",
	"soil_carbon_t2_wo",
	"flu_t2_start",
	"flu_t2_w",
	"flu_t2_wo",
];

const MODULE_TYPE = "Settlements";
const TAG: TagTypes = "Settlements";
const TAG_DEFAULTS: TagTypes = "SettlementsDefaults";
const ENDPOINT = FEModulesObj[FEModules.Settlements].path;

const transformSettlementsResponse = (response: SettlementsResponse) => transformModuleResponse<SettlementsResponse, ModuleFull>({ response, includedPropList, includedT2PropList })

const settlementsApiSlice = createModuleGetApi<ModuleFull>({
    apiSlice,
    path: ENDPOINT,
    moduleName: MODULE_TYPE,
    tag: TAG,
    tagDefaults: TAG_DEFAULTS,
    transformResponse: transformSettlementsResponse,
})

const publicSettlementsApiSlice = createModuleGetApi<ModuleFull>({
    apiSlice: publicApiSlice,
    path: ENDPOINT,
    moduleName: MODULE_TYPE,
    tag: TAG,
    tagDefaults: TAG_DEFAULTS,
    transformResponse: transformSettlementsResponse,
})

export const { useGetSettlementsQuery, useGetSettlementsDefaultsQuery } = settlementsApiSlice;
export const { 
    useGetSettlementsQuery: usePublicGetSettlementsQuery, 
    useGetSettlementsDefaultsQuery: usePublicGetSettlementsDefaultsQuery 
} = publicSettlementsApiSlice;

const ROAD_TYPE = "Roads";
const ROAD_TAG: TagTypes = "Roads";
const ROAD_DEFAULT_TAG: TagTypes = "RoadsDefaults";
const ROAD_ENDPOINT = "roads";

const roadApiSlice = createEntriesGetApi<RoadResponse[]>({
    apiSlice,
    path: ROAD_ENDPOINT,
    moduleName: ROAD_TYPE,
    tag: ROAD_TAG,
    tagDefaults: ROAD_DEFAULT_TAG,
})

const publicRoadApiSlice = createEntriesGetApi<RoadResponse[]>({
    apiSlice: publicApiSlice,
    path: ROAD_ENDPOINT,
    moduleName: ROAD_TYPE,
    tag: ROAD_TAG,
    tagDefaults: ROAD_DEFAULT_TAG,
})

export const { useGetRoadsQuery } = roadApiSlice;
export const { useGetRoadsQuery: usePublicGetRoadsQuery } = publicRoadApiSlice;

const BUILDING_TYPE = "Buildings";
const BUILDING_TAG: TagTypes = "Buildings";
const BUILDING_DEFAULT_TAG: TagTypes = "BuildingsDefaults";
const BUILDING_ENDPOINT = "buildings";

const buildingApiSlice = createEntriesGetApi<BuildingResponse[]>({
    apiSlice,
    path: BUILDING_ENDPOINT,
    moduleName: BUILDING_TYPE,
    tag: BUILDING_TAG,
    tagDefaults: BUILDING_DEFAULT_TAG,
})

const publicBuildingApiSlice = createEntriesGetApi<BuildingResponse[]>({
    apiSlice: publicApiSlice,
    path: BUILDING_ENDPOINT,
    moduleName: BUILDING_TYPE,
    tag: BUILDING_TAG,
    tagDefaults: BUILDING_DEFAULT_TAG,
})

export const { useGetBuildingsQuery } = buildingApiSlice;
export const { useGetBuildingsQuery: usePublicGetBuildingsQuery } = publicBuildingApiSlice;

const OTHER_TYPE = "Others";
const OTHER_TAG: TagTypes = "Others";
const OTHER_DEFAULT_TAG: TagTypes = "OthersDefaults";
const OTHER_ENDPOINT = "other-infrastructures";

const otherApiSlice = createEntriesGetApi<OtherResponse[]>({
    apiSlice,
    path: OTHER_ENDPOINT,
    moduleName: OTHER_TYPE,
    tag: OTHER_TAG,
    tagDefaults: OTHER_DEFAULT_TAG,
})

const publicOtherApiSlice = createEntriesGetApi<OtherResponse[]>({
    apiSlice: publicApiSlice,
    path: OTHER_ENDPOINT,
    moduleName: OTHER_TYPE,
    tag: OTHER_TAG,
    tagDefaults: OTHER_DEFAULT_TAG,
})

export const { useGetOthersQuery } = otherApiSlice;
export const { useGetOthersQuery: usePublicGetOthersQuery } = publicOtherApiSlice;

const moduleApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        updateSettlements: builder.mutation<ModuleFull, { data: ModuleFull, activityId: number }>({
            query: ({ data, activityId }) => {
                const module = { ...data.module };
                const tiertwo = { ...data.tiertwo };
                const lucId = data.module.land_use_change;

                const payload = { ...module, ...tiertwo } as unknown as SettlementsResponse
                const { id, ...rest } = payload
                const body = { ...rest, activity: activityId, land_use_change: lucId }
                convertEmptyStringsToNull(body)
                return {
                    url: `${ENDPOINT}/${id}/`,
                    method: 'PUT',
                    body: body
                }
            },
            invalidatesTags: (_result, _err, { activityId, data }) => [
                { type: TAG, id: data.module.id ?? undefined },
                { type: TAG_DEFAULTS, id: data.module.id ? data.module.id : undefined },
                { type: 'ModuleResults', id: data.module.id ? `settlements-${data.module.id}` : undefined },
                { type: 'ModuleResults', id: "land-use-changes" },
                { type: "ModuleHistory", id: data.module.id ? `settlements-${data.module.id}` : undefined },
                { type: "ModulesStatuses", id: activityId }
            ]
        }),

        createRoads: builder.mutation<RoadResponse, RoadCreatePayload>({
            query: ({ body }) => ({ url: "roads/", method: 'POST', body }),
            invalidatesTags: (_result, _err, data) => [
                { type: 'Roads', id: data.activityId },
                { type: 'ModuleResults', id: data.body.parent ? `settlements-${data.body.parent}` : undefined },
                { type: "ModuleHistory", id: data.body.parent ? `settlements-${data.body.parent}` : undefined },
                { type: "ModulesStatuses", id: data.activityId },
            ]
        }),
        updateRoads: builder.mutation<RoadResponse, RoadPayload>({
            query: ({ body }) => {
                const { id, ...rest } = body
                convertEmptyStringsToNull(rest)
                return { url: `roads/${id}/`, method: 'PUT', body: rest }
            },
            invalidatesTags: (_result, _err, data) => [
                { type: 'Roads', id: data.activityId },
                { type: "ModuleHistory", id: data.body.parent ? `settlements-${data.body.parent}` : undefined },
                { type: "RoadsDefaults", id: data.body.id ? data.body.id : undefined },
                { type: "ModulesStatuses", id: data.activityId }
            ]
        }),
        deleteRoads: builder.mutation<RoadResponse, RoadDeletePayload>({
            query: ({ id }) => ({ url: `roads/${id}/`, method: 'DELETE' }),
            invalidatesTags: (_result, _err, data) => [
                { type: 'Roads', id: data.activityId },
                { type: 'ModuleResults', id: data.parent ? `settlements-${data.parent}` : undefined },
                { type: "ModuleHistory", id: data.parent ? `settlements-${data.parent}` : undefined },
                { type: "RoadsDefaults", id: data.id },
                { type: "ModulesStatuses", id: data.activityId },
            ]
        }),

        createBuildings: builder.mutation<BuildingResponse, RoadCreatePayload>({
            query: ({ body }) => ({ url: "buildings/", method: 'POST', body }),
            invalidatesTags: (_result, _err, data) => [
                { type: 'Buildings', id: data.activityId },
                { type: 'ModuleResults', id: data.body.parent ? `settlements-${data.body.parent}` : undefined },
                { type: "ModuleHistory", id: data.body.parent ? `settlements-${data.body.parent}` : undefined },
                { type: "ModulesStatuses", id: data.activityId },
            ]
        }),
        updateBuildings: builder.mutation<BuildingResponse, BuildingPayload>({
            query: ({ body }) => {
                const { id, ...rest } = body
                convertEmptyStringsToNull(rest)
                return { url: `buildings/${id}/`, method: 'PUT', body: rest }
            },
            invalidatesTags: (_result, _err, data) => [
                { type: 'Buildings', id: data.activityId },
                { type: "ModuleHistory", id: data.body.parent ? `settlements-${data.body.parent}` : undefined },
                { type: "BuildingsDefaults", id: data.body.id ? data.body.id : undefined },
                { type: "ModulesStatuses", id: data.activityId }
            ]
        }),
        deleteBuildings: builder.mutation<BuildingResponse, RoadDeletePayload>({
            query: ({ id }) => ({ url: `buildings/${id}/`, method: 'DELETE' }),
            invalidatesTags: (_result, _err, data) => [
                { type: 'Buildings', id: data.activityId },
                { type: 'ModuleResults', id: data.parent ? `settlements-${data.parent}` : undefined },
                { type: "ModuleHistory", id: data.parent ? `settlements-${data.parent}` : undefined },
                { type: "BuildingsDefaults", id: data.id },
                { type: "ModulesStatuses", id: data.activityId },
            ]
        }),

        createOthers: builder.mutation<OtherResponse, OtherCreatePayload>({
            query: ({ body }) => ({ url: "other-infrastructures/", method: 'POST', body }),
            invalidatesTags: (_result, _err, data) => [
                { type: 'Others', id: data.activityId },
                { type: 'ModuleResults', id: data.body.parent ? `settlements-${data.body.parent}` : undefined },
                { type: "ModuleHistory", id: data.body.parent ? `settlements-${data.body.parent}` : undefined },
                { type: "ModulesStatuses", id: data.activityId },
            ]
        }),
        updateOthers: builder.mutation<OtherResponse, OtherPayload>({
            query: ({ body }) => {
                const { id, ...rest } = body
                convertEmptyStringsToNull(rest)
                return { url: `other-infrastructures/${id}/`, method: 'PUT', body: rest }
            },
            invalidatesTags: (_result, _err, data) => [
                { type: 'Others', id: data.activityId },
                { type: "ModuleHistory", id: data.body.parent ? `settlements-${data.body.parent}` : undefined },
                { type: "OthersDefaults", id: data.body.id ? data.body.id : undefined },
                { type: "ModulesStatuses", id: data.activityId },
            ]
        }),
        deleteOthers: builder.mutation<OtherResponse, OtherDeletePayload>({
            query: ({ id }) => ({ url: `other-infrastructures/${id}/`, method: 'DELETE' }),
            invalidatesTags: (_result, _err, data) => [
                { type: 'Others', id: data.activityId },
                { type: 'ModuleResults', id: data.parent ? `settlements-${data.parent}` : undefined },
                { type: "ModuleHistory", id: data.parent ? `settlements-${data.parent}` : undefined },
                { type: "OthersDefaults", id: data.id },
                { type: "ModulesStatuses", id: data.activityId },
            ]
        }),
    })
})

export const {
    useUpdateSettlementsMutation,

    useCreateRoadsMutation,
    useDeleteRoadsMutation,
    useUpdateRoadsMutation,

    useCreateBuildingsMutation,
    useDeleteBuildingsMutation,
    useUpdateBuildingsMutation,

    useCreateOthersMutation,
    useDeleteOthersMutation,
    useUpdateOthersMutation,
} = moduleApiSlice